import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  CARDIOVASCULAR_ATTRIBUTES,
  MEASUREMENT_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  HORMONE_ATTRIBUTES,
  CANCER_ATTRIBUTES,
  DIABETES_ATTRIBUTES,
  IMMUNOSUPPRESSION_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Shared pre-screening questions for ASCVD studies
 * These questions are common across both Cohort A (Established) and Cohort B (High Risk)
 *
 * CRITICAL: The cardiovascular_event_history question is used to route users to the correct cohort:
 * - "recent_event" (within 12 months) → Routes to Established ASCVD cohort only
 * - "past_event" (over 12 months ago) → Routes to Amgen CVOT only (if BMI ≥27)
 * - "never" → Routes to High Risk cohort only
 */
export const sharedASCVDScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "cardiovascular_event_history",
    type: QuestionType.RADIO,
    question: "Have you ever had a heart attack or stroke?",
    helpText: "This helps us match you to the right study for your health profile.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "recent_event",
        label: "Yes, I have had a heart attack or stroke within the past 12 months",
      },
      {
        value: "past_event",
        label: "Yes, I have had a heart attack or stroke more than 12 months ago",
      },
      {
        value: "never",
        label: "No, I have never had a heart attack or stroke",
      },
    ],
    // No disqualification here - this is used for routing to appropriate cohort
    attributeMapping: mappers.value({
      recent_event: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true },
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: true },
      ],
      past_event: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true },
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: true },
      ],
      never: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: false },
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "ldl_cholesterol_check",
    type: QuestionType.RADIO,
    question: "Have you had your LDL cholesterol checked in the past 6 weeks?",
    helpText: "LDL or low density lipoprotein is one of the subtypes of cholesterol, sometimes called 'bad' cholesterol",
    required: true,
    allowBack: true,
    options: [
      {
        value: "not_tested",
        label: "I have not had my LDL tested in the past 6 weeks",
      },
      {
        value: "cannot_recall",
        label: "I cannot recall my most recent result",
      },
      {
        value: "under_60",
        label: "My recent LDL was <60",
      },
      {
        value: "60_to_74",
        label: "My recent LDL was 60–74",
      },
      {
        value: "75_to_99",
        label: "My recent LDL was 75–99",
      },
      {
        value: "100_or_greater",
        label: "My recent LDL was 100 or greater",
      },
    ],
    attributeMapping: mappers.value({
      not_tested: [
        { attributeDefinitionId: 26821, radioAttributeValue: "000-6t95z9" },
      ],
      cannot_recall: [
        { attributeDefinitionId: 26821, radioAttributeValue: "001-k5vwt" },
      ],
      under_60: [
        { attributeDefinitionId: 26821, radioAttributeValue: "002-under60" },
      ],
      "60_to_74": [
        { attributeDefinitionId: 26821, radioAttributeValue: "003-prioml" },
      ],
      "75_to_99": [
        { attributeDefinitionId: 26821, radioAttributeValue: "004-75to99" },
      ],
      "100_or_greater": [
        { attributeDefinitionId: 26821, radioAttributeValue: "005-sd2mij" },
      ],
    }),
  },

  {
    id: "high_intensity_statin",
    type: QuestionType.RADIO,
    question: "Are you currently taking either Atorvastatin (Lipitor) 40 mg or higher, or Rosuvastatin (Crestor) 20 mg or higher to manage high cholesterol?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am taking one of these high-intensity statins",
      },
      {
        value: "no",
        label: "No, I am not taking these high-intensity statins",
      },
    ],
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 26769, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: 26769, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "major_medical_conditions",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following apply to you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "cancer_recent",
        label: "I have had a cancer diagnosis or treatment within the past 5 years (excluding SCC, BCC, and in-situ cervical cancer)",
        disqualify: true,
      },
      {
        value: "organ_transplant",
        label: "I have had a major organ transplant",
        disqualify: true,
      },
      {
        value: "uncontrolled_diabetes",
        label: "I have uncontrolled diabetes (HbA1c ≥ 9.5%)",
        disqualify: true,
      },
      {
        value: "severe_heart_failure",
        label: "I have severe heart failure",
        disqualify: true,
      },
      {
        value: "end_stage_kidney_disease",
        label: "I have end stage kidney failure or I am on dialysis",
        disqualify: true,
      },
      {
        value: "liver_failure_cirrhosis",
        label: "I have liver failure or cirrhosis",
        disqualify: true,
      },
      {
        value: "hepatitis_b_c",
        label: "I have hepatitis B or hepatitis C",
        disqualify: true,
      },
      {
        value: "pregnancy_breastfeeding",
        label: "I am currently pregnant, breastfeeding, or planning to become pregnant",
        disqualify: true,
      },
      {
        value: "none",
        label: "None of the above is true for me",
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants with these medical conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        cancer_recent: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true },
        ],
        organ_transplant: [
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: true },
        ],
        uncontrolled_diabetes: [
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HBA1C_MORE_THAN_10, booleanAttributeValue: true },
        ],
        severe_heart_failure: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: true },
        ],
        pregnancy_breastfeeding: [
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: true },
        ],
        end_stage_kidney_disease: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true },
        ],
        liver_failure_cirrhosis: [
          { attributeDefinitionId: 72, booleanAttributeValue: true },
        ],
        hepatitis_b_c: [
          { attributeDefinitionId: 834, booleanAttributeValue: true },
          { attributeDefinitionId: 15040, booleanAttributeValue: true },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: false },
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: false },
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HBA1C_MORE_THAN_10, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: false },
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: 72, booleanAttributeValue: false },
          { attributeDefinitionId: 834, booleanAttributeValue: false },
          { attributeDefinitionId: 15040, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "recent_medications",
    type: QuestionType.CHECKBOX,
    question: "Have you recently taken any of the following medications?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "pcsk9_past_3mo",
        label: "I have taken PCSK9 inhibitors (Repatha, Praluent) within the past 3 months",
        disqualify: true,
      },
      {
        value: "gemfibrozil_current",
        label: "I am currently taking gemfibrozil (Lopid)",
        disqualify: true,
      },
      {
        value: "inclisiran_past_18mo",
        label: "I have taken inclisiran (Leqvio) within the past 18 months",
        disqualify: true,
      },
      {
        value: "mipomersen_past_12mo",
        label: "I have taken mipomersen (Kynamro) within the past 12 months",
        disqualify: true,
      },
      {
        value: "lomitapide_past_12mo",
        label: "I have taken lomitapide (Juxtapid) within the past 12 months",
        disqualify: true,
      },
      {
        value: "none",
        label: "None of the above is true for me",
      },
    ],
    disqualifyMessage: "This study requires a washout period from certain cholesterol medications.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        pcsk9_past_3mo: [
          { attributeDefinitionId: 22747, medicationGroupAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        gemfibrozil_current: [
          { attributeDefinitionId: 22272, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        inclisiran_past_18mo: [
          { attributeDefinitionId: 23568, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        mipomersen_past_12mo: [
          { attributeDefinitionId: 26699, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        lomitapide_past_12mo: [
          { attributeDefinitionId: 22806, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: 22747, medicationGroupAttributeValue: { hasTaken: false } },
          { attributeDefinitionId: 22272, medicationAttributeValue: { hasTaken: false } },
          { attributeDefinitionId: 23568, medicationAttributeValue: { hasTaken: false } },
          { attributeDefinitionId: 26699, medicationAttributeValue: { hasTaken: false } },
          { attributeDefinitionId: 22806, medicationAttributeValue: { hasTaken: false } },
        ],
      },
    }),
  },

  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT,
    }),
  },

  {
    ...standardWeightQuestion,
    attributeMapping: mappers.weight({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT,
    }),
  },

  {
    id: "other_clinical_trials",
    type: QuestionType.RADIO,
    question: "Are you currently enrolling in or participating in any other clinical trials?",
    required: true,
    allowBack: true,
    options: [
      { value: "no", label: "No, I am not enrolling in or participating in any other clinical trials" },
      { value: "yes", label: "Yes, I am currently enrolling in or participating in another clinical trial", disqualify: true },
    ],
    disqualifyMessage: "Participants can only participate in one clinical trial at a time.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "inPersonAppointments",
    type: QuestionType.RADIO,
    question: "Can you attend regular in-person study visits over approximately 2-3 years?",
    helpText: "Your local trial site will be located within a reasonable distance from your home, with the exact address provided upon applying to the trial.",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes, I can attend in-person visits" },
      {
        value: "no",
        label: "No, I cannot attend in-person visits",
        disqualify: true,
      },
      {
        value: "discuss",
        label: "I would like to discuss the requirements with the research team",
      },
    ],
    disqualifyMessage: "This study requires regular in-person visits over a 2-3 year period.",
    attributeMapping: mappers.value({
      yes: [],
      no: [],
      discuss: [],
    }),
  },
];
